"use client";

import * as React from "react";
import { CheckIcon, ChevronDown } from "lucide-react";
import * as RPNInput from "react-phone-number-input";
import flags from "react-phone-number-input/flags";

import { Button } from "@/components/ui/button";
import {
    Command,
    CommandEmpty,
    CommandGroup,
    CommandInput,
    CommandItem,
    CommandList,
} from "@/components/ui/command";
import { Input } from "@/components/ui/input";
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from "@/components/ui/popover";
import { ScrollArea } from "@/components/ui/scroll-area";
import { cn } from "@/libs/utils";

type PhoneInputProps = Omit<
    React.ComponentProps<"input">,
    "onChange" | "value" | "ref"
> &
    Omit<RPNInput.Props<typeof RPNInput.default>, "onChange"> & {
        onChange?: (value: RPNInput.Value) => void;
    };

const PhoneInput: React.ForwardRefExoticComponent<PhoneInputProps> =
    React.forwardRef<React.ElementRef<typeof RPNInput.default>, PhoneInputProps>(
        ({ className, onChange, value, ...props }, ref) => {
            return (
                <RPNInput.default
                    ref={ref}
                    className={cn("flex", className)}
                    flagComponent={FlagComponent}
                    countrySelectComponent={CountrySelect}
                    inputComponent={InputComponent}
                    smartCaret={false}
                    value={value || undefined}
                    // Coerce undefined to '' — react-phone-number-input emits it for an
                    // incomplete number.
                    onChange={(value) => onChange?.(value || ("" as RPNInput.Value))}
                    {...props}
                />
            );
        },
    );
PhoneInput.displayName = "PhoneInput";

const InputComponent = React.forwardRef<
    HTMLInputElement,
    React.ComponentProps<"input">
>(({ className, ...props }, ref) => (
    <Input
        className={cn("rounded-e-lg rounded-s-none", className)}
        {...props}
        ref={ref}
    />
));
InputComponent.displayName = "InputComponent";

type CountryEntry = { label: string; value: RPNInput.Country | undefined };

type CountrySelectProps = {
    disabled?: boolean;
    value: RPNInput.Country;
    options: CountryEntry[];
    onChange: (country: RPNInput.Country) => void;
};

const CountrySelect = ({
    disabled,
    value: selectedCountry,
    options: countryList,
    onChange,
}: CountrySelectProps) => {
    const scrollAreaRef = React.useRef<HTMLDivElement>(null);
    const [searchValue, setSearchValue] = React.useState("");
    const [isOpen, setIsOpen] = React.useState(false);

    /**
     * cmdk activates its FIRST item on mount — two highlights, neither under the cursor. Seeding
     * the chosen country collapses that to one and scrolls it into view, not the top of ~250 rows.
     * In state, not a bare value prop: that makes cmdk fully controlled and freezes the highlight.
     */
    const selectedLabel =
        countryList.find((entry) => entry.value === selectedCountry)?.label ?? "";
    const [activeLabel, setActiveLabel] = React.useState(selectedLabel);

    return (
        <Popover
            open={isOpen}
            modal
            onOpenChange={(open) => {
                setIsOpen(open);
                if (open) {
                    setSearchValue("");
                    setActiveLabel(selectedLabel);
                }
            }}
        >
            <PopoverTrigger asChild>
                <Button
                    type="button"
                    variant="outline"
                    className="flex gap-1 rounded-e-none rounded-s-lg border-r-0 px-3"
                    disabled={disabled}
                >
                    <FlagComponent
                        country={selectedCountry}
                        countryName={selectedCountry}
                    />
                    {/* One chevron — redeem.scss rotates it on [data-state='open']. opacity-50
                        is gone: twMerge deduped it against the opacity-100 after it, so it never
                        rendered. Colour is text-theme/60 in redeem.scss, matching FIELD_ICON. */}
                    <ChevronDown className={cn("-mr-2 size-4", disabled && "hidden")} />
                </Button>
            </PopoverTrigger>
            {/* align='start': the default 'center' hangs a 300px panel ~120px past the field. */}
            {/* Opaque from frame 1 — the base fades in from `--tw-enter-opacity: 0` over 250ms,
                and on a 300x330 panel that quarter second reads as "no background". Pinning the
                var keeps the zoom-in. `bg-white` over `bg-popover`; shadow = `shadow-medium`. */}
            <PopoverContent
                align="start"
                className="w-[300px] rounded-[14px] bg-white p-0 shadow-[0_0_15px_0_rgb(0_0_0/0.03),0_2px_30px_0_rgb(0_0_0/0.08),0_0_1px_0_rgb(0_0_0/0.3)]"
                // Inline, not a [--tw-enter-opacity:1] class: (0,1,0) loses to
                // data-[state=open]:fade-in-0 at (0,2,0) — measured, the class left the var at 0.
                style={{ "--tw-enter-opacity": 1 } as React.CSSProperties}
            >
                <Command value={activeLabel} onValueChange={setActiveLabel}>
                    <CommandInput
                        value={searchValue}
                        onValueChange={(value) => {
                            setSearchValue(value);
                            setTimeout(() => {
                                if (scrollAreaRef.current) {
                                    const viewportElement = scrollAreaRef.current.querySelector(
                                        "[data-radix-scroll-area-viewport]",
                                    );
                                    if (viewportElement) {
                                        viewportElement.scrollTop = 0;
                                    }
                                }
                            }, 0);
                        }}
                        placeholder="Search country..."
                    />
                    <CommandList>
                        {/* Thumb rides this className: scroll-area.tsx hard-codes bg-border
                            (#e5e5e5, 1.15:1 here) and renders <ScrollBar /> propless.
                            [data-orientation=vertical] = Radix's track, thumb its only child.
                            theme/60 = #7c9088, 3.1:1. */}
                        <ScrollArea
                            ref={scrollAreaRef}
                            className="h-72 [&_[data-orientation=vertical]>div]:bg-theme/60"
                        >
                            <CommandEmpty>No country found.</CommandEmpty>
                            <CommandGroup>
                                {countryList.map(({ value, label }) =>
                                    value ? (
                                        <CountrySelectOption
                                            key={value}
                                            country={value}
                                            countryName={label}
                                            selectedCountry={selectedCountry}
                                            onChange={onChange}
                                            onSelectComplete={() => setIsOpen(false)}
                                        />
                                    ) : null,
                                )}
                            </CommandGroup>
                        </ScrollArea>
                    </CommandList>
                </Command>
            </PopoverContent>
        </Popover>
    );
};

interface CountrySelectOptionProps extends RPNInput.FlagProps {
    selectedCountry: RPNInput.Country;
    onChange: (country: RPNInput.Country) => void;
    onSelectComplete: () => void;
}

const CountrySelectOption = ({
    country,
    countryName,
    selectedCountry,
    onChange,
    onSelectComplete,
}: CountrySelectOptionProps) => {
    const handleSelect = () => {
        onChange(country);
        onSelectComplete();
    };

    const isChosen = country === selectedCountry;
    const dialCode = `+${RPNInput.getCountryCallingCode(country)}`;

    return (
        <CommandItem
            /**
             * Explicit value so CountrySelect can name the row to activate. cmdk would derive it
             * from the row's text, which the filter also reads — hence keywords, or a dial-code
             * search finds nothing.
             */
            value={countryName}
            keywords={[dialCode]}
            /**
             * Two row states, one element. HOVERED/KEYBOARD-ACTIVE = data-selected (cmdk writes it
             * on pointer-move and arrows alike; no separate hover attr). Stock accent #f5f5f5 =
             * 1.04:1 on white, invisible; theme-200/60 is the Selects' own token.
             * The ! is required — command.tsx spells data-[selected='true'] quoted, twMerge cannot
             * pair them, both land at (0,2,0). CHOSEN plain, so hovered outranks it.
             */
            className={cn(
                "gap-2 data-[selected=true]:!bg-theme-200/60",
                isChosen && "bg-theme-200/25",
            )}
            onSelect={handleSelect}
        >
            <FlagComponent country={country} countryName={countryName} />
            <span className="flex-1 text-sm">{countryName}</span>
            {/* /50 = #858585: 3.7:1 on white, 3.3:1 on a highlighted row — both under 4.5:1. */}
            <span className="text-sm text-foreground/70">{dialCode}</span>
            <CheckIcon
                className={cn("ml-auto size-4 text-theme", isChosen ? "opacity-100" : "opacity-0")}
            />
        </CommandItem>
    );
};

const FlagComponent = ({ country, countryName }: RPNInput.FlagProps) => {
    const Flag = flags[country];

    return (
        <span className="flex h-4 w-6 overflow-hidden rounded-sm bg-foreground/20 [&_svg:not([class*='size-'])]:size-full">
            {Flag && <Flag title={countryName} />}
        </span>
    );
};

export { PhoneInput };
